import React, { useState } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import "./css/monthlySalesVariability.css";

const units = (value) => Number(value || 0).toLocaleString("es-CL", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const money = (value) => new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

function monthLabel(value) {
  return new Intl.DateTimeFormat("es-CL", { month: "short", year: "2-digit", timeZone: "UTC" })
    .format(new Date(value));
}

function ProductMonthlySalesVariability({
  data,
  error,
  loading,
  locations,
  selectedLocation,
  setSelectedLocation,
  products,
  selectedProduct,
  setSelectedProduct,
}) {
  const [activePoint, setActivePoint] = useState(null);
  const series = data?.series || [];
  const max = Number(data?.max_upper_bound || 0) || 1;
  const chartHeight = 240;
  const top = 14;
  const bottom = 38;
  const plotHeight = chartHeight - top - bottom;
  const width = Math.max(780, series.length * 68 + 76);
  const left = 62;
  const plotWidth = width - left - 18;
  const y = (value) => top + plotHeight - (Number(value || 0) / max) * plotHeight;

  return (
    <section className="embedded-dashboard-chart monthly-variability-card product-monthly-card">
      <div className="card-heading monthly-variability-heading">
        <div>
          <p className="card-kicker">Variabilidad por producto</p>
          <h2>Promedio diario mensual del producto</h2>
          <p>Promedio de unidades diarias; la línea vertical representa ±1 desviación estándar.</p>
        </div>
        <div className="product-monthly-filters">
          <CustomSelect
            label="Producto"
            options={products}
            selectedValue={selectedProduct}
            onChange={setSelectedProduct}
            labelExtractor={(item) => item.product}
            valueExtractor={(item) => item.code}
            searchable
            searchPlaceholder="Buscar producto…"
          />
          <CustomSelect
            label="Ubicación"
            options={locations}
            selectedValue={selectedLocation}
            onChange={setSelectedLocation}
            labelExtractor={(item) => item.name}
            valueExtractor={(item) => item.id.toString()}
          />
        </div>
      </div>

      {error ? <div className="dashboard-alert" role="alert">{error}</div> : null}
      {loading ? <div className="dashboard-loading" role="status">Calculando variabilidad del producto…</div> : (
        selectedProduct && series.length ? (
          <div className="monthly-variability-scroll">
            <div className="monthly-variability-stage" style={{ width }} onMouseLeave={() => setActivePoint(null)}>
              <svg
                className="monthly-variability-chart"
                viewBox={`0 0 ${width} ${chartHeight}`}
                role="img"
                aria-label="Promedio diario mensual de unidades del producto con una desviación estándar"
              >
                {[0, 0.5, 1].map((ratio) => {
                  const gridY = y(max * ratio);
                  return (
                    <g key={ratio}>
                      <line x1={left} x2={width - 18} y1={gridY} y2={gridY} className="monthly-grid-line" />
                      <text x={left - 8} y={gridY + 4} textAnchor="end" className="monthly-axis-label">
                        {units(max * ratio)} u.
                      </text>
                    </g>
                  );
                })}
                {series.map((point, index) => {
                  const x = left + ((index + 0.5) / series.length) * plotWidth;
                  return (
                    <g
                      key={point.month}
                      className="monthly-point"
                      tabIndex="0"
                      onMouseEnter={() => setActivePoint({ ...point, x })}
                      onFocus={() => setActivePoint({ ...point, x })}
                    >
                      <title>{`${monthLabel(point.month)} · ${units(point.average_daily_units)} unidades promedio · desviación ${units(point.units_stddev)}`}</title>
                      <line x1={x} x2={x} y1={y(point.upper_bound)} y2={y(point.lower_bound)} className="monthly-whisker" />
                      <line x1={x - 7} x2={x + 7} y1={y(point.upper_bound)} y2={y(point.upper_bound)} className="monthly-whisker-cap" />
                      <line x1={x - 7} x2={x + 7} y1={y(point.lower_bound)} y2={y(point.lower_bound)} className="monthly-whisker-cap" />
                      <circle cx={x} cy={y(point.average_daily_units)} r="5" className="monthly-average-dot" />
                      <text x={x} y={chartHeight - 13} textAnchor="middle" className="monthly-month-label">
                        {monthLabel(point.month)}
                      </text>
                    </g>
                  );
                })}
              </svg>
              {activePoint ? (
                <aside
                  className="chart-product-popover monthly-product-popover product-monthly-popover"
                  style={{ left: Math.min(width - 270, Math.max(8, activePoint.x - 125)) }}
                >
                  <header><strong>{monthLabel(activePoint.month)}</strong><span>Promedio diario</span></header>
                  <div className="monthly-popover-summary">
                    <span>Promedio <strong>{units(activePoint.average_daily_units)} unidades</strong></span>
                    <span>Desviación <strong>±{units(activePoint.units_stddev)} unidades</strong></span>
                    <span>Intervalo <strong>{units(activePoint.lower_bound)} – {units(activePoint.upper_bound)} unidades</strong></span>
                    <span>Ingreso promedio <strong>{money(activePoint.average_daily_revenue)}</strong></span>
                  </div>
                </aside>
              ) : null}
            </div>
          </div>
        ) : <div className="empty-state">No hay ventas del producto para calcular el promedio.</div>
      )}
    </section>
  );
}

export default ProductMonthlySalesVariability;
